import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { messaggioDi } from "@/lib/messaggio-errore";
import { giorno, ora } from "@/lib/crm";
import { SEZIONI } from "@/lib/accessi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Il registro: chi ha cambiato cosa, e quando. Sola lettura, solo titolare (lo
 * dice la RLS, non questa pagina). I prezzi li scrive il database da solo a ogni
 * modifica; gli omaggi li scrive il backend; il Confermo del cliente lascia la sua.
 * Una riga senza autore è una scrittura del sistema (prove, manutenzione).
 */

const FILTRI = [
  { chiave: "tutto", etichetta: "Tutto", tabelle: null },
  { chiave: "prezzi", etichetta: "Prezzi", tabelle: "ricarichi,catene_sconto_acquisto,permessi_livello_ricarico" },
  { chiave: "omaggi", etichetta: "Omaggi", tabelle: "omaggi_catalogo" },
  { chiave: "permessi", etichetta: "Permessi", tabelle: "profili,sedi" },
  { chiave: "contratti", etichetta: "Contratti e incassi", tabelle: "contratti,piano_pagamento,incassi,fatture" },
];
const CATEGORIE = { mobilio: "Mobilio", top: "Top", elettrodomestici: "Elettrodomestici", promozione: "Promozione" };
const RUOLI = { titolare: "titolare", venditore: "venditore", amministrazione: "amministrazione", montatore: "montatore" };
const cifra = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 3, useGrouping: false });
const perc = (v) => (v === undefined || v === null ? "—" : `${cifra.format(Number(v))} %`);
const catena = (s) => (Array.isArray(s) ? s.map((x) => cifra.format(Number(x))).join(" + ") : "—");
const siNo = (b) => (b ? "sì" : "no");

/* Le tabelle che altre parti del gestionale registrano qui: un nome da showroom
   invece del nome della tabella. Quello che non è in elenco esce col suo nome. */
const COSA = {
  contratti: "Contratto",
  piano_pagamento: "Piano di pagamento",
  scadenze_pagamento: "Scadenza di pagamento",
  incassi: "Incasso",
  fatture: "Fattura",
  preventivi: "Preventivo",
  clienti: "Cliente",
  allegati: "Documento del cliente",
  profili: "Collaboratore",
};
const FATTO = { INSERT: "creato", UPDATE: "modificato", DELETE: "tolto" };
const STATI_CONTRATTO = {
  aperto: "bozza", bozza: "bozza", da_firmare: "da firmare", firmato: "firmato", in_produzione: "in produzione",
  consegnato: "consegnato", concluso: "concluso", annullato: "annullato",
};

/** Una riga del registro detta come la direbbe una persona. */
export function descrivi(r) {
  const p = r.before_data || {};
  const d = r.after_data || {};
  switch (r.table_name) {
    case "ricarichi":
      if (r.action === "UPDATE") return `Ricarico ${d.livello}: ${perc(p.percentuale)} → ${perc(d.percentuale)}`;
      return `Ricarico ${d.livello || p.livello}: ${r.action === "INSERT" ? "aggiunto" : "tolto"}`;
    case "catene_sconto_acquisto": {
      const nome = CATEGORIE[d.categoria || p.categoria] || d.categoria || p.categoria;
      return `Sconti d'acquisto ${nome}: ${catena(p.sconti)} → ${catena(d.sconti)}`;
    }
    case "permessi_livello_ricarico":
      return `Livello minimo del ${RUOLI[d.ruolo || p.ruolo] || d.ruolo}: ${p.livello_minimo || "—"} → ${d.livello_minimo || "—"}`;
    case "omaggi_catalogo": {
      const cosa = [];
      if (p.attivo !== d.attivo) cosa.push(`si offre ${siNo(p.attivo)} → ${siNo(d.attivo)}`);
      if (p.default_attivo !== d.default_attivo) cosa.push(`parte acceso ${siNo(p.default_attivo)} → ${siNo(d.default_attivo)}`);
      return `Omaggio «${d.etichetta || p.etichetta || d.codice}»: ${cosa.join(", ") || "modificato"}`;
    }
    case "preventivi":
      if (r.action === "cliente_conferma_preventivo") return "Il cliente ha confermato un preventivo dal link";
      break;
    case "contratti": {
      const numero = d.numero || p.numero;
      const nome = numero ? `Contratto ${numero}` : "Contratto";
      if (r.action === "UPDATE" && p.stato !== d.stato)
        return `${nome}: ${STATI_CONTRATTO[p.stato] || p.stato} → ${STATI_CONTRATTO[d.stato] || d.stato}`;
      break;
    }
    case "profili": {
      const chi = d.nome || p.nome || d.email || p.email;
      if (r.action !== "UPDATE") return `Collaboratore ${chi}: ${r.action === "INSERT" ? "aggiunto" : "tolto"}`;
      const sezioni = (x) => SEZIONI.filter((z) => (x || []).includes(z.id)).map((z) => z.label).join(", ") || "nessuna";
      const cosa = [];
      if (p.ruolo !== d.ruolo) cosa.push(`ruolo ${RUOLI[p.ruolo] || p.ruolo} → ${RUOLI[d.ruolo] || d.ruolo}`);
      if (p.attivo !== d.attivo) cosa.push(d.attivo ? "accesso ridato" : "accesso tolto");
      if (sezioni(p.sezioni_spente) !== sezioni(d.sezioni_spente)) cosa.push(`sezioni spente: ${sezioni(d.sezioni_spente)}`);
      if (JSON.stringify(p.sedi_viste ?? null) !== JSON.stringify(d.sedi_viste ?? null))
        cosa.push(d.sedi_viste ? `vede ${d.sedi_viste.length === 1 ? "una sede sola" : "le sedi scelte"}` : "vede tutte le sedi");
      return `Permessi di ${chi}: ${cosa.join(", ") || "modificato"}`;
    }
    case "sedi":
      if (r.action === "UPDATE" && p.responsabile_id !== d.responsabile_id)
        return `Sede ${d.nome}: ${d.responsabile_id ? "nuovo responsabile" : "senza responsabile"}`;
      break;
    default:
      break;
  }
  const nome = COSA[r.table_name] || r.table_name;
  return FATTO[r.action] ? `${nome} ${FATTO[r.action]}` : `${nome} · ${r.action}`;
}

/** Le righe, in giorni (arrivano dalla più recente): «Oggi · mer 28 ott», «mar 27 ott»… */
function perGiorno(righe) {
  const oggi = giorno(new Date().toISOString());
  const gruppi = [];
  for (const r of righe) {
    const g = giorno(r.created_at);
    if (gruppi[gruppi.length - 1]?.giorno !== g) gruppi.push({ giorno: g, titolo: g === oggi ? `Oggi · ${g}` : g, righe: [] });
    gruppi[gruppi.length - 1].righe.push(r);
  }
  return gruppi;
}

export default function RegistroTab() {
  const [filtro, setFiltro] = useState("tutto");
  const [righe, setRighe] = useState(null);
  const [altre, setAltre] = useState(false);
  const [errore, setErrore] = useState(null);
  const [carica, setCarica] = useState(false);
  /* Cambiando filtro in fretta, una risposta vecchia può arrivare dopo quella
     nuova e ricoprirla: il filtro dice «Prezzi» e sotto c'è tutto. Vale solo
     l'ultima richiesta partita. */
  const ultima = useRef(0);

  const leggi = useCallback(
    async (primaDi) => {
      const mia = ++ultima.current;
      setCarica(true);
      setErrore(null);
      try {
        const tabelle = FILTRI.find((f) => f.chiave === filtro)?.tabelle;
        const r = await api.get("/impostazioni/registro", {
          params: { limite: 30, prima_di: primaDi || undefined, tabella: tabelle || undefined },
        });
        if (mia !== ultima.current) return;
        setRighe((xs) => (primaDi ? [...(xs || []), ...r.data.righe] : r.data.righe));
        setAltre(r.data.altre);
      } catch (e) {
        if (mia === ultima.current) setErrore(messaggioDi(e));
      } finally {
        if (mia === ultima.current) setCarica(false);
      }
    },
    [filtro],
  );

  useEffect(() => {
    setRighe(null);
    leggi();
  }, [leggi]);

  return (
    <Card className="p-card" data-testid="registro-tab">
      <h2 className="text-base font-semibold text-foreground">Registro delle modifiche</h2>
      <p className="mt-micro max-w-2xl text-sm text-muted-foreground">
        Chi ha cambiato cosa, e quando. Non si modifica e non si cancella.
      </p>
      <div className="mt-group flex flex-wrap gap-inside" role="group" aria-label="Filtra il registro">
        {FILTRI.map((f) => (
          <button
            key={f.chiave}
            type="button"
            onClick={() => setFiltro(f.chiave)}
            aria-pressed={filtro === f.chiave}
            data-testid={`registro-filtro-${f.chiave}`}
            className={cn(
              "h-11 rounded-md border px-group text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-9",
              filtro === f.chiave
                ? "border-primary bg-brand-subtle font-medium text-brand-subtle-foreground"
                : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {f.etichetta}
          </button>
        ))}
      </div>

      {errore ? <p className="mt-group text-sm text-danger-foreground">{errore}</p> : null}
      {!righe && !errore ? <Skeleton className="mt-group h-48 w-full rounded-lg" /> : null}
      {righe && righe.length === 0 ? (
        <p className="mt-group rounded-md bg-muted px-group py-group text-sm text-muted-foreground">
          Ancora nessuna modifica registrata qui.
        </p>
      ) : null}
      {righe && righe.length ? (
        <div className="mt-group space-y-group" data-testid="registro-righe">
          {perGiorno(righe).map((g) => (
            <section key={g.giorno} aria-label={g.titolo}>
              <h3 className="text-small font-semibold text-muted-foreground">{g.titolo}</h3>
              <ul className="-mx-card mt-inside divide-y border-y">
                {g.righe.map((r) => (
                  <li key={r.id} className="flex gap-group px-card py-group" data-testid={`registro-riga-${r.id}`}>
                    <span className="w-12 shrink-0 text-small tabular-nums text-muted-foreground">{ora(r.created_at)}</span>
                    <div className="min-w-0 flex-1 sm:flex sm:items-baseline sm:justify-between sm:gap-group">
                      <p className="min-w-0 break-words text-sm text-foreground">{descrivi(r)}</p>
                      <p className="mt-micro min-w-0 break-words text-xs text-muted-foreground sm:mt-0 sm:shrink-0">
                        {r.actor_email === "cliente-anonimo" ? "il cliente" : r.chi || "sistema"}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : null}
      {altre ? (
        <Button
          variant="outline"
          className="mt-group h-11 sm:h-9"
          disabled={carica}
          onClick={() => leggi(righe[righe.length - 1]?.id)}
          data-testid="registro-altre"
        >
          {carica ? "Carico…" : "Mostra le precedenti"}
        </Button>
      ) : null}
    </Card>
  );
}
